import { create } from 'zustand';

/** One new customer message shown as a card. */
export interface MessageAlert {
  id: string;
  /** The inbox's conversation key: `<platform>:<conversation id>`, or the event id. */
  conversationKey: string;
  platform: string;
  fromName: string | null;
  text: string | null;
  at: number;
}

const SOUND_KEY = 'unipostudio.inbox.sound';
/** Cards on screen at once; older ones give way. */
export const MAX_ALERTS = 3;

function readSound(): boolean {
  try {
    return localStorage.getItem(SOUND_KEY) !== 'off';
  } catch {
    return true;
  }
}

interface MessageAlertsStore {
  /** The chime for new customer messages; on unless turned off (per browser). */
  soundOn: boolean;
  alerts: MessageAlert[];
  /** Messages that arrived while the tab was hidden: shown in the tab title. */
  unseen: number;
  setSoundOn: (on: boolean) => void;
  push: (alert: MessageAlert) => void;
  dismiss: (id: string) => void;
  clear: () => void;
  addUnseen: () => void;
  resetUnseen: () => void;
}

export const useMessageAlerts = create<MessageAlertsStore>((set) => ({
  soundOn: readSound(),
  alerts: [],
  unseen: 0,
  setSoundOn: (on) => {
    try {
      localStorage.setItem(SOUND_KEY, on ? 'on' : 'off');
    } catch {
      // ignore
    }
    set({ soundOn: on });
  },
  push: (alert) =>
    set((s) => {
      // A second message in the same conversation replaces its card.
      const rest = s.alerts.filter((a) => a.conversationKey !== alert.conversationKey && a.id !== alert.id);
      return { alerts: [alert, ...rest].slice(0, MAX_ALERTS) };
    }),
  dismiss: (id) => set((s) => ({ alerts: s.alerts.filter((a) => a.id !== id) })),
  clear: () => set({ alerts: [] }),
  addUnseen: () => set((s) => ({ unseen: s.unseen + 1 })),
  resetUnseen: () => set({ unseen: 0 }),
}));

/** The conversation key of an `inbox_events` row, as the server groups it. */
export function conversationKeyOf(row: Record<string, unknown>): string | null {
  const id = typeof row.id === 'string' ? row.id : null;
  const platform = typeof row.platform === 'string' ? row.platform.toLowerCase() : null;
  const conv = typeof row.conversation_id === 'string' && row.conversation_id !== '' ? row.conversation_id : null;
  if (platform && conv) return `${platform}:${conv}`;
  return id;
}

/** A realtime `inbox_events` INSERT worth telling the team about: a customer's message, not ours, not spam. */
export function alertFromRow(row: Record<string, unknown>, now: number = Date.now()): MessageAlert | null {
  if (row.direction !== 'INBOUND' || row.is_spam === true || row.is_archived === true) return null;
  const key = conversationKeyOf(row);
  if (!key || typeof row.id !== 'string' || typeof row.platform !== 'string') return null;
  return {
    id: row.id,
    conversationKey: key,
    platform: row.platform,
    fromName: typeof row.from_name === 'string' && row.from_name.trim() !== '' ? row.from_name : null,
    text: typeof row.text === 'string' && row.text.trim() !== '' ? row.text : null,
    at: now,
  };
}
